import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Slide,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close,
  GetApp,
  Smartphone,
  TabletMac,
  DesktopMac,
  CheckCircle
} from '@mui/icons-material';

const InstallAppPrompt = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deviceType, setDeviceType] = useState('mobile');
  const [operatingSystem, setOperatingSystem] = useState('unknown');

  // Функции для работы с cookies
  const setCookie = (name, value, days) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  useEffect(() => {
    // Определяем операционную систему
    const detectOS = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      
      if (/android/.test(userAgent)) {
        console.log('📱 Обнаружен Android');
        setOperatingSystem('android');
      } else if (/iphone|ipad|ipod/.test(userAgent)) {
        console.log('🍎 Обнаружен iOS');
        setOperatingSystem('ios');
      } else if (/windows/.test(userAgent)) {
        console.log('🪟 Обнаружен Windows');
        setOperatingSystem('windows');
      } else if (/macintosh|mac os x/.test(userAgent)) {
        console.log('🍎 Обнаружен macOS');
        setOperatingSystem('macos');
      } else if (/linux/.test(userAgent)) {
        console.log('🐧 Обнаружен Linux');
        setOperatingSystem('linux');
      } else {
        console.log('❓ Операционная система не определена');
        setOperatingSystem('unknown');
      }
    };

    // Определяем тип устройства
    if (isMobile) {
      setDeviceType('mobile');
    } else if (isTablet) {
      setDeviceType('tablet');
    } else {
      setDeviceType('desktop');
    }

    detectOS();

    // Логируем информацию об устройстве и ОС
    console.log('🔍 Информация об устройстве:');
    console.log('- deviceType:', deviceType);
    console.log('- operatingSystem:', operatingSystem);
    console.log('- userAgent:', navigator.userAgent);

    // Проверяем, установлено ли уже приложение
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone === true) {
        console.log('✅ Приложение уже установлено (standalone mode)');
        setIsInstalled(true);
        return;
      }
      
      // Проверяем по другим признакам
      if (window.screen.height - window.innerHeight < 100) {
        console.log('✅ Приложение уже установлено (скрыта адресная строка)');
        setIsInstalled(true);
        return;
      }
      
      console.log('ℹ️ Приложение не установлено');
    };

    checkIfInstalled();

    // Слушаем событие beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      console.log('🎯 Событие beforeinstallprompt получено!');
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Слушаем событие appinstalled
    const handleAppInstalled = () => {
      console.log('✅ Событие appinstalled получено!');
      setIsInstalled(true);
      setShowPrompt(false);
      setShowSuccess(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Показываем промпт через 5 секунд после загрузки страницы (только для мобильных)
    if ((isMobile || isTablet) && !isInstalled) {
      const timer = setTimeout(() => {
        console.log('⏰ Показываем промпт через 5 секунд');
        setShowPrompt(true);
      }, 5000);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isMobile, isTablet, isInstalled, deviceType, operatingSystem]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('✅ Пользователь принял установку приложения');
          setShowSuccess(true);
        } else {
          console.log('❌ Пользователь отклонил установку приложения');
        }
        
        setDeferredPrompt(null);
        setShowPrompt(false);
      } catch (error) {
        console.error('❌ Ошибка при установке:', error);
        showManualInstallInstructions();
      }
    } else {
      showManualInstallInstructions();
    }
  };

  const showManualInstallInstructions = () => {
    const instructions = getInstallInstructions();
    alert(instructions);
  };

  const getInstallInstructions = () => {
    if (operatingSystem === 'android') {
      return `📱 Добавить на главный экран (Android):

1. Нажмите на меню браузера (⋮) в правом верхнем углу
2. Выберите "Добавить на главный экран" или "Установить приложение"
3. Нажмите "Добавить"

После установки приложение появится на главном экране!`;
    } else if (operatingSystem === 'ios') {
      return `🍎 Добавить на главный экран (iOS):

1. Нажмите на кнопку "Поделиться" (□↑) внизу экрана
2. Выберите "На экран «Домой»" или "Добавить на главный экран"
3. Нажмите "Добавить"

После установки приложение появится на главном экране!`;
    } else if (operatingSystem === 'windows') {
      return `🪟 Добавить на главный экран (Windows):

1. Нажмите на меню браузера (⋮) в правом верхнем углу
2. Выберите "Установить приложение" или "Создать ярлык"
3. Следуйте инструкциям установки

После установки приложение появится в меню Пуск!`;
    } else if (operatingSystem === 'macos') {
      return `🍎 Добавить в Dock (macOS):

1. Нажмите на меню браузера в правом верхнем углу
2. Выберите "Установить приложение" или "Добавить в Dock"
3. Следуйте инструкциям установки

После установки приложение появится в Dock!`;
    } else {
      return `💻 Добавить на главный экран:

1. Найдите в меню браузера "Установить приложение"
2. Или "Добавить на главный экран"
3. Или "Создать ярлык"

После установки приложение появится на главном экране!`;
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
    // Сохраняем в cookies на неделю, чтобы не показывать снова
    setCookie('installPromptDismissed', Date.now().toString(), 7);
    // Также сохраняем в localStorage для совместимости
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Сохраняем в cookies на неделю, чтобы не показывать снова
    setCookie('installPromptDismissed', Date.now().toString(), 7);
    // Также сохраняем в localStorage для совместимости
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  // Проверяем, не был ли промпт отклонен недавно (раз в неделю)
  useEffect(() => {
    // Проверяем сначала cookies, потом localStorage
    const dismissedCookie = getCookie('installPromptDismissed');
    const dismissedLocalStorage = localStorage.getItem('installPromptDismissed');
    
    const dismissed = dismissedCookie || dismissedLocalStorage;
    
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 дней в миллисекундах
      
      if (Date.now() - dismissedTime < oneWeek) {
        console.log('🚫 Промпт был отклонен недавно, не показываем');
        setShowPrompt(false);
      }
    }
  }, []);

  // Не показываем промпт, если приложение уже установлено или это десктоп
  if (isInstalled || (!isMobile && !isTablet) || !showPrompt) {
    return null;
  }

  const getDeviceIcon = () => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone fontSize="large" />;
      case 'tablet':
        return <TabletMac fontSize="large" />;
      default:
        return <DesktopMac fontSize="large" />;
    }
  };

  const getDeviceText = () => {
    switch (deviceType) {
      case 'mobile':
        return 'мобильное устройство';
      case 'tablet':
        return 'планшет';
      default:
        return 'устройство';
    }
  };

  return (
    <>
      <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            p: 2,
            pb: { xs: 2, sm: 3 } // Больше отступ снизу для безопасной зоны
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              p: 3,
              background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
              border: '1px solid rgba(0,0,0,0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Декоративный элемент */}
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
                opacity: 0.1,
                zIndex: 0
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Заголовок */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2196F3, #21CBF3)',
                    color: 'white',
                    mr: 2
                  }}
                >
                  {getDeviceIcon()}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {operatingSystem === 'android' ? 'Добавить на главный экран (Android)' :
                     operatingSystem === 'ios' ? 'Добавить на главный экран (iOS)' :
                     operatingSystem === 'windows' ? 'Установить приложение (Windows)' :
                     operatingSystem === 'macos' ? 'Добавить в Dock (macOS)' :
                     'Добавить на главный экран'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Быстрый доступ к ZARA
                  </Typography>
                </Box>
              </Box>

              {/* Описание */}
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                Добавьте приложение на {getDeviceText()} для быстрого доступа к каталогу ZARA. 
                Получайте уведомления о новых поступлениях и акциях.
              </Typography>

              

              {/* Кнопки */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* Основная кнопка */}
                <Button
                  variant="contained"
                  startIcon={<GetApp />}
                  onClick={handleInstall}
                  fullWidth={isMobile}
                  sx={{
                    background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                    borderRadius: 2,
                    py: 1.5,
                    px: 3,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388E3C, #4CAF50)',
                      boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                    }
                  }}
                >
                  {deferredPrompt ? 'Установить автоматически' : 'Показать инструкции'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleDismiss}
                  fullWidth={isMobile}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 3,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: '#e0e0e0',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: '#bdbdbd',
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  Позже
                </Button>
              </Box>
            </Box>

            {/* Кнопка закрытия */}
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              <Close />
            </IconButton>
          </Paper>
        </Box>
      </Slide>

      {/* Уведомление об успешной установке */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          icon={<CheckCircle />}
          sx={{ width: '100%' }}
        >
          Приложение успешно установлено! Теперь у вас есть быстрый доступ к ZARA.
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstallAppPrompt;
